import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiRequestService } from './api-request.service';
import { UtilsService } from './utils.service';

/**
 * Payload returned by the generic `GET v2/background-exports/{id}` endpoint.
 * Mirrors the shape produced by `BackgroundExportsController::show`.
 */
export interface BackgroundExportStatus {
  id: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  feature: string;
  format: string;
  file_name: string | null;
  started_at: number | null;
  completed_at: number | null;
  error_message: string | null;
  file_url: string | null;
}

/**
 * Optional hooks callers can pass so a feature can customise toast copy or
 * observe each lifecycle transition without re-implementing the polling
 * machinery.
 */
export interface BackgroundExportTrackOptions {
  /** Toast shown immediately after polling starts. Defaults to a generic "being prepared" message. */
  preparingMessage?: string;
  /** Toast shown when the polling budget is exhausted. Defaults to a "check your email" fallback. */
  timeoutMessage?: string;
  /**
   * Override the polling budget (in milliseconds) for this specific export.
   * Defaults to the service-level budget (10 minutes) when omitted. Use this
   * for features that are known to produce large exports.
   */
  pollTimeoutMs?: number;
  /** Called when the export completes. If it returns false, the default download is skipped. */
  onCompleted?: (status: BackgroundExportStatus) => boolean | void;
  /** Called when the export fails. Return false to suppress the default failure toast. */
  onFailed?: (status: BackgroundExportStatus, sanitisedMessage: string) => boolean | void;
}

/**
 * Generic client-side lifecycle tracker for any background export that
 * persists a BackgroundExport record on the API. Features should call
 * `track(exportId)` after dispatching the export; the service handles
 * polling, timeout, auto-download, and failure toasts in one place.
 *
 * The service is root-provided so it lives for the life of the Angular
 * application. Polling loops deliberately survive component navigation: a
 * user who triggers an export on the Tasks page and then navigates to
 * another page still gets their auto-download when the job completes. Host
 * components should NOT call `cancelAll()` in their ngOnDestroy: let the
 * loop continue, and the email plus the "My Exports" list remain as
 * fallbacks.
 *
 * Each polling loop stops automatically when:
 *   - The export transitions to `completed` or `failed`, or
 *   - The per-loop `pollTimeoutMs` budget is exhausted (default 10 minutes).
 *
 * Callers can override the budget per export via
 * `BackgroundExportTrackOptions.pollTimeoutMs` for features that legitimately
 * produce long-running exports.
 *
 * See .kiro/specs/task-exports-fix/design.md (Defect 4, Tasks 11.1 to 11.5).
 */
@Injectable({ providedIn: 'root' })
export class BackgroundExportService {
  /**
   * Active polling handles keyed by export id. Each entry owns its own
   * interval so concurrent exports poll independently.
   */
  private polls: Map<number, { intervalHandle: any; startedAt: number; pollTimeoutMs: number }> = new Map();

  /** Polling cadence in milliseconds. */
  private readonly pollIntervalMs: number = 2500;

  /**
   * Default polling budget. Any individual poll can override this via
   * `BackgroundExportTrackOptions.pollTimeoutMs`. The mailable remains as a
   * fallback after the budget elapses.
   */
  private readonly defaultPollTimeoutMs: number = 10 * 60 * 1000;

  /** Sanitised error messages longer than this are truncated for toast display. */
  private readonly maxErrorToastLength: number = 200;

  /**
   * Number of active polling loops. Emits on every start / stop so UI
   * surfaces (e.g. the top-nav activity indicator) can react without
   * polling the service themselves.
   */
  private activeCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  /** Observable stream of the active polling count. */
  public activeCount$: Observable<number> = this.activeCountSubject.asObservable();

  /** Convenience boolean stream: true when at least one poll is active. */
  public hasActive$: Observable<boolean> = this.activeCount$.pipe(map((count) => count > 0));

  constructor(
    private api: ApiRequestService,
    private utils: UtilsService
  ) {}

  /**
   * Start polling the status endpoint for `exportId` until the record is
   * completed or failed, or the polling budget elapses. Shows a "preparing"
   * toast immediately, triggers a browser download on completion, and shows
   * a sanitised error toast on failure.
   *
   * Calling `track` twice with the same id is a no-op: the existing poll
   * continues without being disturbed.
   */
  track(exportId: number, options: BackgroundExportTrackOptions = {}): void {
    if ( this.polls.has(exportId) ) {
      return;
    }

    this.utils.showToast(
      options.preparingMessage
      || 'Your export is being prepared. The file will download automatically when ready.'
    );

    const startedAt: number = Date.now();
    const pollTimeoutMs: number = typeof options.pollTimeoutMs === 'number' && options.pollTimeoutMs > 0
      ? options.pollTimeoutMs
      : this.defaultPollTimeoutMs;
    const intervalHandle: any = setInterval(() => {
      this.pollOnce(exportId, options);
    }, this.pollIntervalMs);

    this.polls.set(exportId, { intervalHandle, startedAt, pollTimeoutMs });
    this.emitActiveCount();
  }

  /**
   * Stop polling for a single export id. Safe to call for ids that are not
   * being tracked.
   */
  cancel(exportId: number): void {
    const entry = this.polls.get(exportId);
    if ( entry ) {
      clearInterval(entry.intervalHandle);
      this.polls.delete(exportId);
      this.emitActiveCount();
    }
  }

  /**
   * Cancel every active polling loop. Exposed for testing and emergency
   * teardown. Host components should NOT call this in ngOnDestroy because
   * it defeats the cross-navigation persistence guarantee that features
   * rely on.
   */
  cancelAll(): void {
    this.polls.forEach((entry) => {
      clearInterval(entry.intervalHandle);
    });
    this.polls.clear();
    this.emitActiveCount();
  }

  /**
   * Emit the current size of the polls map to subscribers.
   */
  private emitActiveCount(): void {
    this.activeCountSubject.next(this.polls.size);
  }

  /**
   * Run a single status request and handle the response (or ignore transient
   * failures so the loop keeps going).
   */
  private pollOnce(exportId: number, options: BackgroundExportTrackOptions): void {
    const entry = this.polls.get(exportId);
    if ( !entry ) {
      return;
    }
    if ( Date.now() - entry.startedAt >= entry.pollTimeoutMs ) {
      this.cancel(exportId);
      this.utils.showToast(
        options.timeoutMessage
        || 'Your export is still being prepared. Please check your email for the download link.'
      );
      return;
    }

    this.api.makeRequest('get', `v2/background-exports/${exportId}`).then((response) => {
      const payload: BackgroundExportStatus | null = response && response.data ? response.data : null;
      if ( !payload ) {
        return;
      }

      if ( payload.status === 'completed' && payload.file_url ) {
        this.cancel(exportId);
        const shouldDownload: boolean = options.onCompleted ? options.onCompleted(payload) !== false : true;
        if ( shouldDownload ) {
          this.triggerDownload(payload.file_url, payload.file_name);
        }
        return;
      }

      if ( payload.status === 'failed' ) {
        this.cancel(exportId);
        const sanitised: string = this.sanitiseError(payload.error_message);
        const shouldToast: boolean = options.onFailed ? options.onFailed(payload, sanitised) !== false : true;
        if ( shouldToast ) {
          this.utils.showToast(`Your export could not be prepared. ${sanitised}`);
        }
      }
    }).catch(() => {
      // Ignore transient errors and keep polling until the timeout budget is
      // reached. Surfacing errors on every tick would be noisy.
    });
  }

  /**
   * Trigger a browser download by programmatically clicking a hidden anchor
   * element. Works against presigned temporary URLs that carry a
   * Content-Disposition header.
   */
  private triggerDownload(fileUrl: string, fileName: string | null): void {
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.href = fileUrl;
    if ( fileName ) {
      anchor.download = fileName;
    }
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  /**
   * Normalise an error message so it is safe to render in a toast: trim,
   * strip newlines, cap length, and fall back to a friendly generic
   * message when the server supplied nothing useful.
   */
  private sanitiseError(errorMessage: string | null | undefined): string {
    const fallback: string = 'Please try again or contact support.';
    if ( !errorMessage ) {
      return fallback;
    }
    const cleaned: string = String(errorMessage).replace(/[\r\n]+/g, ' ').trim();
    if ( cleaned.length === 0 ) {
      return fallback;
    }
    return cleaned.length > this.maxErrorToastLength
      ? cleaned.substring(0, this.maxErrorToastLength)
      : cleaned;
  }
}
